import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { EditableComponent } from '@adobe/aem-react-editable-components'
import {
  ButtonVariants,
  NearbyStores as UIFoundationsNearbyStores,
  NearbyStoresProps as UIFoundationsNearbyStoresProps,
  StoreCardProps
} from '@dcxwam/dcx-wam-ui-foundations'
import {
  createStoresSDK,
  getStoreList,
  getStores,
  SortField,
  SortOrder,
  type Store,
  type StoreListParams
} from '../../../../services/stores/stores-sdk'
import NearByStoresEditConfig from './NearByStoresEditConfig'
import { NearByStoresProps } from './types'
import { validateStoreImages } from '../common/store-image-utils'
import { useGeolocation } from '../../../../utils/geo-location'
import withComponentRefresh from '../../../../utils/aem/editor/withComponentRefresh'
import { CbxConfigType, PageConfig } from '../../../../utils/page-config'
import {
  buildDataLayerStoreObj,
  createStoreDetailUrl,
  getCategoryDetails,
  getCurrentDayOpenCloseMessage,
  getFormattedAddress
} from '../../StoreDetails/utils'
import { pushDataLayer } from '../../../../utils/datalayer'
import { OpenStatus } from '../../StoreDetails/types'
import { getAkamaiHeaders } from '../../../../utils/api-headers'
import { canUseDom } from '../../../../utils/isSsr'
import { generateStoreLocationLDJson } from './schema.utils'
import {
  withErrorBoundary,
  ErrorFallback
} from '../../../functional/ErrorBoundary'

if (canUseDom) {
  // eslint-disable-next-line no-void
  void import('@dcxwam/dcx-wam-ui-foundations/css')
}

const NearByStores: FC<{ model: NearByStoresProps }> = withComponentRefresh(
  ({ model, ...rest }) => {
    const {
      latitude,
      longitude,
      isLoading: locationLoading,
      error: locationError,
      isAuthor
    } = useGeolocation()

    const {
      storeLocatorConfig,
      enableStoreSpecificImages,
      initialValidatedImages,
      storeImage,
      storeTypes,
      maxItems,
      viewStoreDetailsLabel
    } = model

    const [stores, setStores] = useState<Store[]>(model.initialStores || [])
    const [validatedImages, setValidatedImages] = useState<Map<string, string>>(
      () => {
        if (!initialValidatedImages) {
          return new Map()
        }

        if (initialValidatedImages instanceof Map) {
          return initialValidatedImages
        }

        if (
          typeof initialValidatedImages === 'object' &&
          !Array.isArray(initialValidatedImages)
        ) {
          return new Map(Object.entries(initialValidatedImages))
        }

        return new Map()
      }
    )

    const {
      categories,
      openedUntilLabel,
      willOpenAtLabel,
      openingHoursNotAvailable,
      days,
      enable12HourFormat,
      storeDetailPageUrl,
      platformBrands,
      storeAddressFormatTemplate
    } = storeLocatorConfig || {}

    const getStoreStatus = useCallback(
      (store: Store) => {
        const currentStatus = getCurrentDayOpenCloseMessage(
          store?.openingHours || [],
          {
            labels: {
              openedUntilLabel,
              willOpenAtLabel,
              openingHoursNotAvailable
            },
            days,
            enable12HourFormat
          }
        ) || {
          status: OpenStatus.Closed,
          message: ''
        }

        return {
          content: currentStatus.message ?? '',
          openCloseStatus: currentStatus.status
        }
      },
      [openedUntilLabel, willOpenAtLabel, days, enable12HourFormat]
    )

    const transformStores = useCallback(
      (storesData: Store[], addressTemplate?: string): StoreCardProps[] => {
        return storesData.map((store) => {
          const imageUrl =
            enableStoreSpecificImages &&
            store.storeId &&
            validatedImages.has(store.storeId)
              ? validatedImages.get(store.storeId)
              : storeImage?.image?.src

          return {
            iconUrl:
              getCategoryDetails(categories, store?.type ?? '')?.image ?? '',
            name: store.name ?? '',
            address: getFormattedAddress(store.address, addressTemplate) ?? '',
            statusProps: getStoreStatus(store),
            ...(viewStoreDetailsLabel && {
              buttonProps: {
                buttonVariant: ButtonVariants.Outlined,
                label: viewStoreDetailsLabel,
                onClick: () =>
                  pushDataLayer(
                    buildDataLayerStoreObj('storeLocatorDetailsClick', store)
                  )
              }
            }),
            url: createStoreDetailUrl(store, storeDetailPageUrl ?? ''),
            imageProps: {
              width: '100%',
              src: imageUrl
            }
          }
        })
      },
      [
        enableStoreSpecificImages,
        validatedImages,
        categories,
        getStoreStatus,
        viewStoreDetailsLabel,
        storeDetailPageUrl
      ]
    )

    useEffect(() => {
      const { cbxConfig, lang } = PageConfig.getConfig() as {
        cbxConfig: CbxConfigType
        lang: string
      }

      if (locationError || locationLoading || !cbxConfig || isAuthor) {
        return
      }

      const fetchStores = async () => {
        if (!latitude || !longitude) {
          return
        }

        const {
          cbxUrl: baseCbxUrl,
          market: cbxMarket,
          applicationId,
          loggerEnabled
        } = cbxConfig

        const params: StoreListParams = {
          latitude: Number(latitude),
          longitude: Number(longitude),
          distance: 500,
          sortField: SortField.DISTANCE,
          sortOrder: SortOrder.ASC,
          ...(storeTypes &&
            storeTypes.length > 0 && { storeInfoType: storeTypes }),
          ...(maxItems && { pageSize: maxItems }),
          ...(platformBrands && { platformBrands: platformBrands.split(',') })
        }

        const response = await getStoreList(
          createStoresSDK({
            baseCbxUrl,
            market: cbxMarket,
            lang,
            applicationId,
            loggerEnabled
          }),
          params
        )

        if (
          response &&
          'items' in response &&
          Array.isArray(response.items) &&
          response.items.length > 0
        ) {
          setStores(response.items)
          if (enableStoreSpecificImages) {
            const validatedImagesMap = await validateStoreImages(
              response.items,
              storeImage?.image?.src
            )
            setValidatedImages(validatedImagesMap)
          }
        }
      }

      fetchStores()
    }, [
      latitude,
      longitude,
      locationLoading,
      locationError,
      isAuthor,
      storeTypes,
      maxItems,
      platformBrands,
      enableStoreSpecificImages
    ])

    const storeCards = useMemo(
      () => transformStores(stores, storeAddressFormatTemplate),
      [stores, storeAddressFormatTemplate, transformStores]
    )

    const nearbyStoresProps: UIFoundationsNearbyStoresProps = {
      prevSlideMessage: model.leftArrowAriaLabel,
      nextSlideMessage: model.rightArrowAriaLabel,
      stores: storeCards
    }

    const editableProps = { model, ...rest }

    return (
      <EditableComponent {...editableProps}>
        {NearByStoresEditConfig.isEmpty(model) ? (
          NearByStoresEditConfig.emptyLabel
        ) : (
          <UIFoundationsNearbyStores {...nearbyStoresProps} />
        )}
      </EditableComponent>
    )
  }
)

// @ts-expect-error Property 'getServerSideProps' does not exist
NearByStores.getServerSideProps = async (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props: Record<string, any>
) => {
  const { fallbackStores, storeImage, enableStoreSpecificImages, ...rest } =
    props

  const { cbxConfig, lang } = PageConfig.getConfig() as {
    cbxConfig: CbxConfigType
    lang: string
  }

  if (!fallbackStores?.length || !cbxConfig) {
    return {
      ...rest,
      fallbackStores,
      storeImage,
      enableStoreSpecificImages,
      initialStores: []
    }
  }

  const { cbxUrl: baseCbxUrl, market, applicationId, loggerEnabled } = cbxConfig
  const storesSDK = createStoresSDK({
    baseCbxUrl,
    market,
    lang,
    applicationId,
    loggerEnabled,
    fresh: true,
    ...getAkamaiHeaders()
  })

  const storeIds = fallbackStores.filter(Boolean) as string[]

  const initialStores = await getStores(storesSDK, storeIds)

  let initialValidatedImages: Record<string, string> | undefined

  if (enableStoreSpecificImages && initialStores?.length) {
    const validatedImagesMap = await validateStoreImages(
      initialStores,
      storeImage?.image?.src
    )
    initialValidatedImages = Object.fromEntries(validatedImagesMap)
  }

  return {
    ...rest,
    fallbackStores,
    storeImage,
    enableStoreSpecificImages,
    initialStores: initialStores || [],
    ...(initialValidatedImages && { initialValidatedImages })
  }
}

// @ts-expect-error Property 'getHeadData' does not exist
NearByStores.getHeadData = async ({ ...props }) => {
  const {
    seoTitle,
    seoDescription,
    initialStores,
    storeLocatorConfig,
    storeImage,
    enableStoreSpecificImages,
    initialValidatedImages
  } = props ?? {}

  const jsonLD = generateStoreLocationLDJson({
    stores: initialStores || [],
    pageName: seoTitle,
    pageDescription: seoDescription,
    pageUrl: props.pageUrl,
    storeImageUrl: storeImage?.image?.src,
    storeDetailPageUrl: storeLocatorConfig?.storeDetailPageUrl,
    enableStoreSpecificImages,
    validatedImages: initialValidatedImages
  })

  return {
    status: 200,
    seoTitle,
    seoDescription,
    jsonLD
  }
}

const NearByStoresWithErrorBoundary = withErrorBoundary(NearByStores, {
  componentName: 'NearByStores',
  fallback: <ErrorFallback componentName="NearByStores" />
})

export default NearByStoresWithErrorBoundary
